import { Navigate, Route } from 'react-router-dom';
import {
  CUSTOMER_PERMISSIONS,
  SALES_PERMISSIONS,
} from '@invento/shared';
import { PermissionGuard } from '@/navigation/guards/permission-guard';
import { lazyNamed } from '../lazy-named';
import { TenantPreviewRouteFrame } from './route-frames';
import { CompatibilityRedirect } from '../compatibility-redirect';

const loadCustomers = () => import('@/features/customers');
const CustomerDetailPage = lazyNamed(loadCustomers, 'CustomerDetailPage');
const CustomerEditPage = lazyNamed(loadCustomers, 'CustomerEditPage');
const CustomerEditRouteGuard = lazyNamed(loadCustomers, 'CustomerEditRouteGuard');
const CustomerNewPage = lazyNamed(loadCustomers, 'CustomerNewPage');
const CustomersPage = lazyNamed(loadCustomers, 'CustomersPage');

const loadSales = () => import('@/features/sales');
const PosReceiptDetailPage = lazyNamed(loadSales, 'PosReceiptDetailPage');
const PosReceiptsListPage = lazyNamed(loadSales, 'PosReceiptsListPage');
const PosShiftReviewsPage = lazyNamed(loadSales, 'PosShiftReviewsPage');
const SalesCreditNoteCreatePage = lazyNamed(loadSales, 'SalesCreditNoteCreatePage');
const SalesCreditNoteDetailPage = lazyNamed(loadSales, 'SalesCreditNoteDetailPage');
const SalesCreditNotesPage = lazyNamed(loadSales, 'SalesCreditNotesPage');
const SalesInvoiceDetailPage = lazyNamed(loadSales, 'SalesInvoiceDetailPage');
const SalesInvoicesPage = lazyNamed(loadSales, 'SalesInvoicesPage');
const SalesNewInvoicePage = lazyNamed(loadSales, 'SalesNewInvoicePage');
const SalesNewOrderPage = lazyNamed(loadSales, 'SalesNewOrderPage');
const SalesNewQuotePage = lazyNamed(loadSales, 'SalesNewQuotePage');
const SalesOrderDetailPage = lazyNamed(loadSales, 'SalesOrderDetailPage');
const SalesOrdersPage = lazyNamed(loadSales, 'SalesOrdersPage');
const SalesPaymentNewPage = lazyNamed(loadSales, 'SalesPaymentNewPage');
const SalesQuotesPage = lazyNamed(loadSales, 'SalesQuotesPage');
const SalesQuoteDetailPage = lazyNamed(loadSales, 'SalesQuoteDetailPage');
const SalesReturnCreatePage = lazyNamed(loadSales, 'SalesReturnCreatePage');
const SalesReturnDetailPage = lazyNamed(loadSales, 'SalesReturnDetailPage');
const SalesReturnsPage = lazyNamed(loadSales, 'SalesReturnsPage');
const SalesStatementNewPage = lazyNamed(loadSales, 'SalesStatementNewPage');

export function salesCustomerRoutes() {
  return (
    <Route element={<TenantPreviewRouteFrame />}>
      <Route
        path="sales"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <SalesInvoicesPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/invoices/new"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.INVOICE_CREATE}>
            <SalesNewInvoicePage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/invoices/:invoiceId"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <SalesInvoiceDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/quotes"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <SalesQuotesPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/quotes/new"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.QUOTE_CREATE}>
            <SalesNewQuotePage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/quotes/:quoteId"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <SalesQuoteDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/orders"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <SalesOrdersPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/orders/new"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.ORDER_CREATE}>
            <SalesNewOrderPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/orders/:orderId"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <SalesOrderDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/returns"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.RETURN_READ}>
            <SalesReturnsPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/returns/new"
        element={
          <PermissionGuard requiredAll={[SALES_PERMISSIONS.RETURN_CREATE, SALES_PERMISSIONS.READ]}>
            <SalesReturnCreatePage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/returns/:returnId"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.RETURN_READ}>
            <SalesReturnDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/credit-notes"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.CREDIT_NOTE_READ}>
            <SalesCreditNotesPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/credit-notes/new"
        element={
          <PermissionGuard requiredAll={[SALES_PERMISSIONS.CREDIT_NOTE_CREATE, SALES_PERMISSIONS.READ]}>
            <SalesCreditNoteCreatePage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/credit-notes/:creditNoteId"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.CREDIT_NOTE_READ}>
            <SalesCreditNoteDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/statements/new"
        element={
          <PermissionGuard
            requiredAll={[
              CUSTOMER_PERMISSIONS.STATEMENT_READ,
              SALES_PERMISSIONS.STATEMENT_CREATE,
              SALES_PERMISSIONS.STATEMENT_SEND,
            ]}
          >
            <SalesStatementNewPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/payments/new"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.PAYMENT_CREATE}>
            <SalesPaymentNewPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/pos/receipts"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <PosReceiptsListPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/pos/shift-reviews"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.POS_SHIFT_VARIANCE_APPROVE}>
            <PosShiftReviewsPage />
          </PermissionGuard>
        }
      />
      <Route
        path="sales/pos/receipts/:receiptId"
        element={
          <PermissionGuard required={SALES_PERMISSIONS.READ}>
            <PosReceiptDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="customers"
        element={
          <PermissionGuard required={CUSTOMER_PERMISSIONS.READ}>
            <CustomersPage />
          </PermissionGuard>
        }
      />
      <Route
        path="customers/new"
        element={
          <PermissionGuard required={CUSTOMER_PERMISSIONS.CREATE}>
            <CustomerNewPage />
          </PermissionGuard>
        }
      />
      <Route
        path="customers/create"
        element={
          <CompatibilityRedirect to="/customers/new" variant="renamed" toLabel="New customer" />
        }
      />
      <Route
        path="customers/:customerId/edit"
        element={
          <CustomerEditRouteGuard>
            <CustomerEditPage />
          </CustomerEditRouteGuard>
        }
      />
      <Route
        path="customers/:customerId"
        element={
          <PermissionGuard required={CUSTOMER_PERMISSIONS.READ}>
            <CustomerDetailPage />
          </PermissionGuard>
        }
      />
    </Route>
  );
}
